import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sparkles, TrendingUp, Target, Store, MapPin, CalendarRange, RefreshCw,
  Loader2, Trophy, Flame, ArrowRight, Users, CalendarPlus, ExternalLink,
  Info, Check, CalendarClock, ChevronRight, Shuffle, Lock, Compass, ChevronDown, CalendarDays,
} from "lucide-react";

const card = "rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";

const HORIZONS = [
  { v: "4", label: "1 mois (4 sem.)" },
  { v: "8", label: "2 mois (8 sem.)" },
  { v: "12", label: "3 mois (12 sem.)" },
  { v: "26", label: "6 mois (26 sem.)" },
  { v: "52", label: "12 mois (52 sem.)" },
];

// Dates RÉELLES et lisibles (jamais « 31/08 »). Ex : « lun. 7 oct. » / « lundi 7 octobre 2026 ».
function frShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  } catch { return String(iso).slice(0, 10); }
}
// Date courte AVEC l'année (ex. « sam. 22 août 2025 ») — indispensable pour l'historique.
function frShortDateYear(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}
function frFullDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}
function frMonthYear(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch { return ""; }
}

// Couleur de cellule selon l'excellence (seuil BS/j) ou l'état.
function cellTheme(cell) {
  if (cell.state === "planned") {
    return { border: "var(--border-soft)", bg: "rgba(24,24,24,0.03)", ink: "#475569", accent: "#64748B" };
  }
  if (cell.excellent) {
    return { border: "rgba(31,157,85,0.38)", bg: "rgba(31,157,85,0.08)", ink: "#166534", accent: "#1F9D55" };
  }
  return { border: "rgba(238,114,46,0.34)", bg: "rgba(238,114,46,0.07)", ink: "#8A3A10", accent: "#EE722E" };
}

const TIER_LABEL = { top: "Top perf", bon: "Bon", moyen: "Moyen" };

function KpiCard({ icon: Icon, label, value, suffix, tint, testid }) {
  return (
    <div className={`${card} flex items-center gap-3 p-4`} data-testid={testid}>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: tint.bg, color: tint.fg }}>
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-ink">{label}</div>
        <div className="text-xl font-extrabold text-ink">
          {value}{suffix ? <span className="ml-0.5 text-sm font-bold text-muted-ink">{suffix}</span> : null}
        </div>
      </div>
    </div>
  );
}

// Carte de REPLI (historique du planning idéal). Reste visible même quand le créneau
// est « déjà planifié » : permet au CDP de choisir un secours si le magasin principal
// refuse. On garde EXACTEMENT la même nomenclature que les cartes proposées (couleur
// verte si excellent, badge TOP PERF, provenance Perf. asso / Réf. autre asso).
function IdealAltCard({ cell, ligneAssoc, rank, onOpenSpot }) {
  const t = cellTheme(cell);
  const scopeAssoc = cell.perf_scope === "association";
  const shownAvg = scopeAssoc && cell.assoc_avg_per_day != null ? cell.assoc_avg_per_day : cell.avg_per_day;
  const shownBs = Math.round(shownAvg || 0);
  const best = cell.best_assoc;
  const acr = ligneAssoc?.acronym || "cette asso";
  return (
    <button
      onClick={() => onOpenSpot(cell.spot_id)}
      data-testid={`ideal-altcard-${cell.spot_id}`}
      className="flex w-full rounded-xl border p-2 text-left transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2"
      style={{ borderColor: t.border, background: t.bg, borderLeftWidth: 3, borderLeftColor: t.accent }}>
      <div className="min-w-0 flex-1 text-left">
        <div className="flex items-center justify-between gap-1">
          <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold"
            style={{ background: `${t.accent}22`, color: t.ink }}>
            {cell.excellent ? <Flame className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />} ≈ {shownBs} BS/j
          </span>
          <span className="inline-flex shrink-0 items-center gap-1">
            {cell.tier && <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: t.accent }}>{TIER_LABEL[cell.tier] || ""}</span>}
            <span className="grid h-4 w-4 place-items-center rounded-full text-[9px] font-extrabold"
              style={{ background: `${t.accent}22`, color: t.accent }} title={`Repli n°${rank}`}>{rank}</span>
          </span>
        </div>
        <div className="mt-1 break-words text-[12px] font-bold leading-tight text-ink" title={cell.spot_name}>{cell.spot_name}</div>
        <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink">
          <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" />
          <span className="break-words">{[cell.ville, cell.dept ? `(${cell.dept})` : null].filter(Boolean).join(" ")}</span>
        </div>
        <div className="mt-1">
          {scopeAssoc ? (
            <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold"
              style={{ background: "rgba(31,157,85,0.14)", color: "#166534" }} title={`Historique de ${acr} sur ce magasin`}>
              <Check className="h-2.5 w-2.5" /> Perf. {acr}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold"
              style={{ background: "rgba(148,110,50,0.14)", color: "#8A5A16" }}
              title={best ? `Aucun historique ${acr} ici — performance réalisée par ${best.acronym}` : "Performance toutes associations"}>
              <Info className="h-2.5 w-2.5" /> {best ? `Réf. ${best.acronym}` : "Toutes assos"}
            </span>
          )}
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-1">
          <span className="inline-flex items-center gap-1 rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-ink ring-1 ring-inset ring-[var(--border-soft)]">
            <CalendarClock className="h-2.5 w-2.5" />{frShortDate(cell.suggested_date)}
          </span>
          <ChevronRight className="h-3.5 w-3.5" style={{ color: t.accent }} />
        </div>
      </div>
    </button>
  );
}

// Cellule de proposition avec popover détaillé (rendement + top recruteurs + CTA).
function IdealCell({ cell, weekMeta, onOpenSpot, onBook, minAvg, ligneAssoc, onlyNew, onLock }) {
  const [recs, setRecs] = useState(undefined); // undefined=non chargé, []=chargé vide
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [locking, setLocking] = useState(false);

  const loadRecs = async () => {
    if (recs !== undefined || !cell.spot_id) return;
    setLoadingRecs(true);
    try {
      const { data } = await api.get(`/prospection/spot-history/${cell.spot_id}`);
      setRecs(data?.top_recruteurs || []);
    } catch { setRecs([]); }
    finally { setLoadingRecs(false); }
  };

  const handleLock = async () => {
    if (!onLock) return;
    setLocking(true);
    try { await onLock(); } finally { setLocking(false); }
  };

  const isNewImplant = cell.state === "proposed" && cell.perf_scope === "global";

  if (cell.state === "none" || (onlyNew && !isNewImplant)) {
    return (
      <div className="flex min-h-[92px] items-center justify-center rounded-xl border border-dashed border-[var(--border-soft)]"
        data-testid={`ideal-cell-none-${weekMeta.monday}`}>
        <span className="text-xs font-semibold text-muted-ink/50">{onlyNew && cell.state !== "none" ? "—" : "Aucun spot dispo"}</span>
      </div>
    );
  }

  const t = cellTheme(cell);
  const isPlanned = cell.state === "planned";
  const alternatives = cell.alternatives || [];
  const altCount = alternatives.length;
  const scopeAssoc = cell.perf_scope === "association";
  const shownAvg = scopeAssoc && cell.assoc_avg_per_day != null ? cell.assoc_avg_per_day : cell.avg_per_day;
  const shownBs = Math.round(shownAvg || 0);
  const best = cell.best_assoc; // meilleure asso sur ce spot
  const acr = ligneAssoc?.acronym || "cette asso";

  // Étiquette de PROVENANCE de la performance (asso de la ligne vs autre asso).
  const scopeTag = isPlanned ? null : (
    scopeAssoc ? (
      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold"
        style={{ background: "rgba(31,157,85,0.14)", color: "#166534" }}
        title={`Historique de ${acr} sur ce magasin`}>
        <Check className="h-2.5 w-2.5" /> Perf. {acr}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold"
        style={{ background: "rgba(148,110,50,0.14)", color: "#8A5A16" }}
        title={best ? `Aucun historique ${acr} ici — performance réalisée par ${best.acronym}` : "Performance toutes associations"}>
        <Info className="h-2.5 w-2.5" /> {best ? `Réf. ${best.acronym}` : "Toutes assos"}
      </span>
    )
  );

  const body = (
    <div className="min-w-0 text-left">
      <div className="flex items-center justify-between gap-1">
        <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold"
          style={{ background: `${t.accent}22`, color: t.ink }}>
          {isPlanned ? <Check className="h-3 w-3" /> : (cell.excellent ? <Flame className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />)}
          ≈ {shownBs} BS/j
        </span>
        {cell.tier && !isPlanned && (
          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide" style={{ color: t.accent }}>{TIER_LABEL[cell.tier] || ""}</span>
        )}
      </div>
      <div className="mt-1 break-words text-[12px] font-bold leading-tight text-ink" title={cell.spot_name}>{cell.spot_name}</div>
      <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink">
        <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" />
        <span className="break-words">{[cell.ville, cell.dept ? `(${cell.dept})` : null].filter(Boolean).join(" ")}</span>
      </div>
      {scopeTag && <div className="mt-1">{scopeTag}</div>}
      <div className="mt-1.5 flex items-center justify-between gap-1">
        <span className="inline-flex items-center gap-1 rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] font-semibold text-ink ring-1 ring-inset ring-[var(--border-soft)]">
          <CalendarClock className="h-2.5 w-2.5" />{frShortDate(cell.suggested_date)}
        </span>
        {isPlanned
          ? <span className="shrink-0 text-[9px] font-bold uppercase text-[#64748B]">Déjà planifié</span>
          : (
            <span className="inline-flex shrink-0 items-center gap-1">
              {altCount > 0 && (
                <span className="rounded-full bg-white/80 px-1 text-[9px] font-bold text-muted-ink ring-1 ring-inset ring-[var(--border-soft)]" title={`${altCount} emplacement(s) de repli`}>+{altCount}</span>
              )}
              <ChevronRight className="h-3.5 w-3.5" style={{ color: t.accent }} />
            </span>
          )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-[92px] flex-col gap-1.5" data-testid={`ideal-cellwrap-${weekMeta.monday}-${cell.spot_id}`}>
    <Popover onOpenChange={(o) => { if (o) loadRecs(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid={`ideal-cell-${weekMeta.monday}-${cell.spot_id}`}
          className="flex min-h-[92px] w-full rounded-xl border p-2 text-left transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2"
          style={{ borderColor: t.border, background: t.bg, borderLeftWidth: 3, borderLeftColor: t.accent }}>
          {body}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={12} className="w-[min(20rem,calc(100vw-1.5rem))] p-3" data-testid={`ideal-pop-${cell.spot_id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="break-words text-sm font-extrabold leading-tight text-ink" title={cell.spot_name}>{cell.spot_name}</div>
            <div className="mt-0.5 flex items-start gap-1 text-[11px] text-muted-ink">
              <MapPin className="mt-0.5 h-3 w-3 shrink-0" />{[cell.ville, cell.dept ? `(${cell.dept})` : null].filter(Boolean).join(" ")}
            </div>
          </div>
          <span className="shrink-0 rounded-lg px-2 py-1 text-center" style={{ background: `${t.accent}18`, color: t.ink }}>
            <span className="block text-lg font-extrabold leading-none">{shownAvg}</span>
            <span className="block text-[8px] font-bold uppercase">BS / jour</span>
          </span>
        </div>

        {/* Date proposée — réelle et complète */}
        <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-[var(--surface-2)] px-2 py-1.5 text-[11px] font-semibold capitalize text-ink">
          <CalendarClock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          {frFullDate(cell.suggested_date)}
        </div>

        {/* Provenance de la performance : asso de la ligne vs autre asso */}
        {!isPlanned && (
          scopeAssoc ? (
            <div className="mt-2 rounded-lg border border-[rgba(31,157,85,0.3)] bg-[rgba(31,157,85,0.07)] px-2 py-1.5 text-[11px] leading-snug text-[#166534]" data-testid="ideal-pop-scope">
              <b>Performance de {acr} ici</b> : {cell.assoc_avg_per_day} BS/j
              {cell.assoc_bulletins ? ` · ${cell.assoc_bulletins} bulletin${cell.assoc_bulletins > 1 ? "s" : ""} sur ${cell.assoc_days} jour${cell.assoc_days > 1 ? "s" : ""}` : ""}.
            </div>
          ) : (
            <div className="mt-2 rounded-lg border border-[rgba(148,110,50,0.3)] bg-[rgba(148,110,50,0.07)] px-2 py-1.5 text-[11px] leading-snug text-[#8A5A16]" data-testid="ideal-pop-scope">
              <b>Aucun historique {acr}</b> sur ce magasin. Le rendement affiché ({cell.avg_per_day} BS/j) est celui{best ? <> de <b>{best.acronym}</b> ({best.avg_per_day} BS/j · {best.bulletins} BS)</> : " toutes associations"}. À proposer donc en <b>nouvelle implantation</b> pour {acr}.
            </div>
          )
        )}

        <div className="my-2 grid grid-cols-2 gap-1.5 text-center">
          <div className="rounded-lg bg-[var(--surface-2)] px-1 py-1.5">
            <div className="text-sm font-extrabold text-ink">{cell.valid ?? "—"}</div>
            <div className="text-[9px] font-semibold uppercase text-muted-ink">BS valides</div>
          </div>
          <div className="rounded-lg bg-[var(--surface-2)] px-1 py-1.5">
            <div className="text-sm font-extrabold text-ink">{cell.days ?? "—"}</div>
            <div className="text-[9px] font-semibold uppercase text-muted-ink">jours</div>
          </div>
        </div>
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-[var(--surface-2)] px-2 py-1.5">
          <span className="text-[9px] font-semibold uppercase text-muted-ink">Dernier passage réalisé</span>
          <span className="text-[11px] font-extrabold text-ink">{cell.last_date ? frShortDateYear(cell.last_date) : "—"}</span>
        </div>

        <div className="rounded-lg border border-[var(--border-soft)] p-2">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
            <Users className="h-3 w-3" /> Meilleurs recruteurs ici
          </div>
          {loadingRecs && <div className="flex items-center gap-1.5 py-1 text-[11px] text-muted-ink"><Loader2 className="h-3 w-3 animate-spin" /> Chargement…</div>}
          {!loadingRecs && Array.isArray(recs) && recs.length === 0 && (
            <div className="py-1 text-[11px] text-muted-ink">Aucun historique nominatif.</div>
          )}
          {!loadingRecs && Array.isArray(recs) && recs.slice(0, 4).map((r, i) => (
            <div key={i} className="flex items-start justify-between gap-2 py-0.5 text-[11px]">
              <span className="min-w-0 break-words font-semibold leading-snug text-ink">{r.name}</span>
              <span className="shrink-0 text-muted-ink">{r.bulletins} BS · {r.avg_per_day}/j</span>
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex flex-col gap-1.5">
          {isPlanned ? (
            <div className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-[rgba(100,116,139,0.12)] text-[12px] font-bold text-[#475569]">
              <Check className="h-3.5 w-3.5" /> Déjà planifié
            </div>
          ) : (
            <Button size="sm" className="h-8 w-full justify-center gap-1.5 bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]"
              data-testid={`ideal-lock-${cell.spot_id}`} onClick={handleLock} disabled={locking}>
              {locking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
              {locking ? "Verrouillage…" : "Verrouiller ce créneau"}
            </Button>
          )}
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-8 flex-1 justify-center gap-1.5"
              data-testid={`ideal-fiche-${cell.spot_id}`} onClick={() => onOpenSpot(cell.spot_id)}>
              <ExternalLink className="h-3.5 w-3.5" /> Fiche & appel
            </Button>
            <Button size="sm" variant="ghost" className="h-8 flex-1 justify-center gap-1.5 text-muted-ink"
              data-testid={`ideal-book-${cell.spot_id}`} onClick={() => onBook(weekMeta.monday, cell.spot_id)}>
              <CalendarPlus className="h-3.5 w-3.5" /> Planning
            </Button>
          </div>
        </div>

        {!isPlanned && altCount > 0 && (
          <div className="mt-2.5 border-t border-[var(--border-soft)] pt-2" data-testid={`ideal-alts-${cell.spot_id}`}>
            <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
              <Shuffle className="h-3 w-3" /> Si refus — {altCount} repli{altCount > 1 ? "s" : ""}
            </div>
            <div className="space-y-1">
              {alternatives.map((a, i) => {
                const ag = a.excellent;
                return (
                  <button key={a.spot_id || i} onClick={() => onOpenSpot(a.spot_id)}
                    data-testid={`ideal-alt-${a.spot_id}`}
                    className="flex w-full items-center gap-2 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-left transition-colors hover:border-[var(--shariti-orange)] hover:bg-[var(--surface-2)]">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md text-[10px] font-extrabold"
                      style={{ background: ag ? "rgba(31,157,85,0.12)" : "rgba(238,114,46,0.12)", color: ag ? "#1F7A44" : "#EE722E" }}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block break-words text-[11px] font-semibold leading-tight text-ink" title={a.spot_name}>{a.spot_name}</span>
                      <span className="block break-words text-[9px] capitalize text-muted-ink">{[a.ville, a.dept ? `(${a.dept})` : null].filter(Boolean).join(" ")} · {frShortDate(a.suggested_date)}</span>
                    </span>
                    <span className="shrink-0 text-[10px] font-bold" style={{ color: ag ? "#1F7A44" : "#EE722E" }}>≈{a.expected_bs}</span>
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>

    {/* Historique du planning idéal conservé même si « déjà planifié » : on affiche
        les meilleurs emplacements de repli pour ce jour, prêts à activer si le magasin
        principal refuse finalement l'autorisation. */}
    {isPlanned && altCount > 0 && (
      <div className="space-y-1.5" data-testid={`ideal-planned-alts-${cell.spot_id}`}>
        <div className="flex items-center gap-1 px-0.5 text-[9px] font-bold uppercase tracking-wide text-[#8A5A16]">
          <Shuffle className="h-2.5 w-2.5" /> Replis si refus · {altCount}
        </div>
        {alternatives.map((a, i) => (
          <IdealAltCard key={a.spot_id || i} cell={a} ligneAssoc={ligneAssoc} rank={i + 1} onOpenSpot={onOpenSpot} />
        ))}
      </div>
    )}
    </div>
  );
}

// Carte MOBILE : une ligne = une carte pliable, les semaines listées verticalement
// en pleine largeur (aucun scroll horizontal → aucun chevauchement / troncature).
function MobileLigneCard({ row, weeksMeta, onlyNew, minAvg, onOpenSpot, onBook, onLock, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);

  const entries = row.cells
    .map((cell, ci) => ({ cell, weekMeta: weeksMeta[ci] }))
    .filter(({ cell }) => {
      if (cell.state === "none") return false;
      if (onlyNew) return cell.state === "proposed" && cell.perf_scope === "global";
      return true;
    });

  if (entries.length === 0) return null;

  const proposedCount = entries.filter((e) => e.cell.state === "proposed").length;
  const plannedCount = entries.filter((e) => e.cell.state === "planned").length;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={`${card} overflow-hidden`} data-testid={`ideal-mrow-${row.ligne_id}`}>
      <CollapsibleTrigger asChild>
        <button className="flex w-full items-start gap-2.5 p-3 text-left transition-colors hover:bg-[var(--surface-2)]"
          data-testid={`ideal-mrow-toggle-${row.ligne_id}`}>
          <span className="mt-0.5 inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-extrabold text-white"
            style={{ background: row.assoc.color }} title={row.assoc.name}>{row.assoc.acronym || "—"}</span>
          <div className="min-w-0 flex-1">
            <div className="break-words text-[13px] font-bold leading-tight text-ink">{row.identifiant}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-ink">
              {row.cdp?.name && (
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full border border-black/20" style={{ background: row.cdp.color || "var(--surface-2)" }} />{row.cdp.name}</span>
              )}
              <span className="inline-flex items-center gap-1"><Store className="h-2.5 w-2.5" />{row.canal_nom}</span>
            </div>
            <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink">
              <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" /><span className="break-words">{row.territoire_label}</span>
            </div>
          </div>
          <span className="flex shrink-0 flex-col items-end gap-1">
            <span className="rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 text-[10px] font-extrabold text-[var(--shariti-orange)]">
              {entries.length} créneau{entries.length > 1 ? "x" : ""}
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-ink transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2.5 border-t border-[var(--border-soft)] bg-[var(--bg-subtle)] p-3">
          {(proposedCount > 0 || plannedCount > 0) && (
            <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold text-muted-ink" data-testid={`ideal-mrow-summary-${row.ligne_id}`}>
              {proposedCount > 0 && <span className="rounded-md bg-[rgba(238,114,46,0.10)] px-1.5 py-0.5 text-[#8A3A10]">{proposedCount} à proposer</span>}
              {plannedCount > 0 && <span className="rounded-md bg-[rgba(100,116,139,0.12)] px-1.5 py-0.5 text-[#475569]">{plannedCount} déjà planifié{plannedCount > 1 ? "s" : ""}</span>}
            </div>
          )}
          {entries.map(({ cell, weekMeta }) => (
            <div key={weekMeta.monday}>
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-semibold text-muted-ink">
                <span className="capitalize text-ink">{frShortDate(weekMeta.monday)}</span>
                <span>Semaine {weekMeta.week_number}</span>
              </div>
              <IdealCell cell={cell} weekMeta={weekMeta} onOpenSpot={onOpenSpot} onBook={onBook}
                minAvg={minAvg} ligneAssoc={row.assoc} onlyNew={false}
                onLock={() => onLock(row, cell)} />
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Cellule « mois » : agenda compact du mois pour une ligne. On liste CHAQUE proposition
// hebdo (date précise + emplacement + rendement), au lieu d'un seul « phare », pour que
// la vue soit lisible ET complète. Chaque carte ouvre la fiche du spot au clic.
function MonthCell({ items, onlyNew, minAvg, onOpenSpot }) {
  const bsOf = (c) => ((c.perf_scope === "association" && c.assoc_avg_per_day != null) ? c.assoc_avg_per_day : c.avg_per_day) || 0;
  const entries = (items || []).filter(({ cell }) => {
    if (!cell || cell.state === "none") return false;
    if (onlyNew) return cell.state === "proposed" && cell.perf_scope === "global";
    return cell.state === "proposed" || cell.state === "planned";
  });
  if (entries.length === 0) {
    return (
      <div className="flex min-h-[64px] items-center justify-center rounded-xl border border-dashed border-[var(--border-soft)]">
        <span className="text-xs font-semibold text-muted-ink/40">—</span>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="ideal-monthcol">
      {entries.map(({ cell, weekMeta }, k) => {
        const bs = Math.round(bsOf(cell));
        const green = bs >= (minAvg || 5);
        const planned = cell.state === "planned";
        const accent = planned ? "#94A3B8" : (green ? "#1F9D55" : "#EE722E");
        const softBg = planned ? "rgba(148,163,184,0.10)" : (green ? "rgba(31,157,85,0.06)" : "rgba(238,114,46,0.05)");
        const scopeAssoc = cell.perf_scope === "association";
        return (
          <button key={weekMeta?.monday || k} onClick={() => onOpenSpot(cell.spot_id)}
            data-testid={`ideal-monthcell-${cell.spot_id}-${k}`}
            title={`${frShortDate(cell.suggested_date)} · ${cell.spot_name} · ≈${bs} BS/j`}
            className="flex w-full flex-col rounded-lg border p-1.5 text-left transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: "var(--border-soft)", background: softBg, borderLeftWidth: 3, borderLeftColor: accent }}>
            <div className="flex items-center justify-between gap-1">
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold capitalize" style={{ color: accent }}>
                <CalendarClock className="h-3 w-3 shrink-0" /> {frShortDate(cell.suggested_date)}
              </span>
              <span className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-extrabold" style={{ color: accent }}>
                {!planned && (green ? <Flame className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />)}≈{bs}
              </span>
            </div>
            <div className="mt-0.5 break-words text-[11px] font-bold leading-snug text-ink">{cell.spot_name}</div>
            <div className="mt-0.5 flex items-end justify-between gap-1">
              <span className="break-words text-[9px] capitalize text-muted-ink">
                {[cell.ville, cell.dept ? `(${cell.dept})` : null].filter(Boolean).join(" ")}
              </span>
              {planned ? (
                <span className="shrink-0 rounded px-1 py-0.5 text-[8px] font-bold uppercase text-[#64748B]" style={{ background: "rgba(148,163,184,0.18)" }}>Planifié</span>
              ) : (
                <span className="shrink-0 rounded px-1 py-0.5 text-[8px] font-bold uppercase"
                  style={scopeAssoc ? { background: "rgba(31,157,85,0.14)", color: "#166534" } : { background: "rgba(148,110,50,0.14)", color: "#8A5A16" }}>
                  {scopeAssoc ? "Perf." : "Réf."}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}


export default function PlanningIdeal() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assoc, setAssoc] = useState("all");
  const [weeks, setWeeks] = useState("12");
  const [minBs, setMinBs] = useState("");
  const [onlyNew, setOnlyNew] = useState(false);
  // Affichage de la grille : par semaine (détaillé) ou par mois (compact, vue d'ensemble).
  const [viewMode, setViewMode] = useState("weeks"); // "weeks" | "months"
  const [cdpFilter, setCdpFilter] = useState("all"); // "all" | cdp_id — filtre « ma tournée »

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { weeks: Number(weeks) };
      if (assoc !== "all") params.association_id = assoc;
      if (cdpFilter !== "all") params.cdp_id = cdpFilter;
      if (minBs !== "" && !Number.isNaN(Number(minBs))) params.min_avg = Number(minBs);
      const { data } = await api.get("/planning/ideal", { params });
      setData(data);
      if (minBs === "" && data?.min_avg != null) setMinBs(String(data.min_avg));
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, [assoc, weeks, minBs, cdpFilter]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assoc, weeks, cdpFilter]);

  const k = data?.kpis || {};
  const weeksMeta = data?.weeks || [];
  // Filtre « ma tournée » : n'affiche que les lignes du CDP sélectionné (client-side).
  const displayedRows = (data?.rows || []).filter((r) => cdpFilter === "all" || r.cdp_id === cdpFilter);
  const colW = 208;
  const monthColW = 178;
  const leftW = 288;
  const isMonthly = viewMode === "months";

  const openSpot = (id) => navigate(`/spots/${id}`);
  const bookWeek = (monday) => navigate(`/planning?week=${monday}`);

  // Nombre de « nouvelles implantations » à conquérir (perf d'une autre asso).
  const newCount = useMemo(() => {
    let n = 0;
    (data?.rows || []).forEach((r) => r.cells.forEach((c) => {
      if (c.state === "proposed" && c.perf_scope === "global") n += 1;
    }));
    return n;
  }, [data]);

  // Verrouille un créneau en 1 clic (crée le booking « au crayon ») sans quitter la page.
  const lockSlot = async (row, cell) => {
    try {
      await api.post("/planning/assign", {
        spot_id: cell.spot_id, ligne_id: row.ligne_id, date: cell.suggested_date,
      });
      setData((prev) => {
        if (!prev) return prev;
        const rows = prev.rows.map((r) => {
          if (r.ligne_id !== row.ligne_id) return r;
          return {
            ...r,
            cells: r.cells.map((c) =>
              (c.week === cell.week && c.spot_id === cell.spot_id) ? { ...c, state: "planned" } : c),
          };
        });
        const kpis = {
          ...prev.kpis,
          proposals: Math.max(0, (prev.kpis.proposals || 0) - 1),
          already_planned: (prev.kpis.already_planned || 0) + 1,
        };
        return { ...prev, rows, kpis };
      });
      toast.success(`Créneau verrouillé — ${cell.spot_name} · ${frFullDate(cell.suggested_date)}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) toast.error("Rotation non respectée pour ce créneau. Ouvrez le planning pour forcer si nécessaire.");
      else if (status === 403) toast.error(typeof detail === "string" ? detail : "Vous n'êtes pas autorisé à poser ce créneau.");
      else toast.error("Impossible de verrouiller ce créneau. Réessayez.");
    }
  };

  // Repères de changement de mois pour la ligne d'en-tête.
  const monthSpans = useMemo(() => {
    const spans = [];
    weeksMeta.forEach((w, i) => {
      const label = frMonthYear(w.monday);
      const prev = spans[spans.length - 1];
      if (prev && prev.label === label) prev.count += 1;
      else spans.push({ label, count: 1, startIdx: i });
    });
    return spans;
  }, [weeksMeta]);

  return (
    <>
      <PageHeader
        title="Planning Idéal"
        subtitle={`Le planning que vous devriez viser sur ${(HORIZONS.find((h) => h.v === weeks)?.label || "3 mois").split(" (")[0]} — basé sur le rendement réel des emplacements.`}
        action={
          <Button variant="outline" onClick={load} disabled={loading} data-testid="ideal-refresh" className="gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Actualiser
          </Button>
        }
      />
      <PageBody>
        {/* Bandeau stratégie */}
        <div className="mb-4 overflow-hidden rounded-2xl border border-[rgba(238,114,46,0.25)] p-4"
          style={{ background: "linear-gradient(120deg, rgba(238,114,46,0.10), rgba(252,219,83,0.10))" }}
          data-testid="ideal-strategy">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--shariti-orange)] text-white">
              <Sparkles className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-extrabold text-ink">Passez d'un planning subi à un planning choisi.</div>
              <p className="mt-0.5 text-[12px] leading-snug text-muted-ink">
                Pour chaque ligne et chaque semaine, nous proposons le <b>meilleur emplacement disponible</b> (rendement
                historique le plus élevé), en respectant automatiquement la <b>règle de rotation</b> et la <b>contrainte Paris</b>.
                Appelez ces magasins et proposez-leur <b>ces dates précises</b> pour cartonner.
              </p>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="mb-4 grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-end">
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Association</label>
            <Select value={assoc} onValueChange={setAssoc}>
              <SelectTrigger className="h-9 w-full sm:w-[190px]" data-testid="ideal-filter-assoc"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les associations</SelectItem>
                {(data?.associations || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.acronym}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">CDP (ma tournée)</label>
            <Select value={cdpFilter} onValueChange={setCdpFilter}>
              <SelectTrigger className="h-9 w-full sm:w-[190px]" data-testid="ideal-filter-cdp"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les CDP</SelectItem>
                {(data?.cdps || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: c.color }} />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Horizon</label>
            <Select value={weeks} onValueChange={setWeeks}>
              <SelectTrigger className="h-9 w-full sm:w-[170px]" data-testid="ideal-filter-weeks"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HORIZONS.map((h) => <SelectItem key={h.v} value={h.v}>{h.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Seuil « excellent » (BS/j)</label>
            <div className="flex items-center gap-1.5">
              <Input type="number" step="0.5" min="0" value={minBs} onChange={(e) => setMinBs(e.target.value)}
                className="h-9 w-full sm:w-[90px]" data-testid="ideal-filter-minbs" />
              <Button variant="outline" className="h-9 shrink-0" onClick={load} data-testid="ideal-apply-minbs">OK</Button>
            </div>
          </div>
          <div className="w-full sm:ml-auto sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Affichage</label>
            <div className="inline-flex h-9 w-full items-center rounded-lg border border-[var(--border-soft)] bg-white p-0.5 sm:w-auto" data-testid="ideal-viewmode">
              <button
                onClick={() => setViewMode("weeks")}
                data-testid="ideal-view-weeks"
                className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-bold transition-colors sm:flex-none ${!isMonthly ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <CalendarClock className="h-3.5 w-3.5" /> Semaines
              </button>
              <button
                onClick={() => setViewMode("months")}
                data-testid="ideal-view-months"
                title="Vue compacte mois par mois — idéale pour lire jusqu'à 12 mois d'un coup d'œil"
                className={`flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-bold transition-colors sm:flex-none ${isMonthly ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                <CalendarDays className="h-3.5 w-3.5" /> Mois
              </button>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <label className="mb-1 block text-[11px] font-semibold text-muted-ink">Vue</label>
            <Button
              variant={onlyNew ? "default" : "outline"}
              onClick={() => setOnlyNew((v) => !v)}
              data-testid="ideal-filter-new"
              className={`h-9 w-full justify-center gap-1.5 sm:w-auto ${onlyNew ? "bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]" : ""}`}
              title="N'afficher que les magasins performants pour une AUTRE association — à conquérir pour la vôtre">
              <Compass className="h-4 w-4" />
              Nouvelles implantations{data ? ` (${newCount})` : ""}
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[76px] rounded-2xl" />)
          ) : (
            <>
              <KpiCard icon={TrendingUp} label="BS attendus" value={k.total_expected_bs ?? 0} suffix="/ jour cumulé"
                tint={{ bg: "rgba(31,157,85,0.12)", fg: "#1F7A44" }} testid="ideal-kpi-bs" />
              <KpiCard icon={Sparkles} label="Emplacements proposés" value={k.proposals ?? 0}
                tint={{ bg: "rgba(238,114,46,0.12)", fg: "#EE722E" }} testid="ideal-kpi-proposals" />
              <KpiCard icon={Target} label="Couverture" value={k.coverage_pct ?? 0} suffix="%"
                tint={{ bg: "rgba(59,108,183,0.12)", fg: "#3B6CB7" }} testid="ideal-kpi-coverage" />
              <KpiCard icon={Store} label="BS moyen / spot" value={k.avg_expected_bs ?? 0} suffix="BS/j"
                tint={{ bg: "rgba(124,58,237,0.12)", fg: "#7C3AED" }} testid="ideal-kpi-avg" />
            </>
          )}
        </div>

        {/* Légende */}
        {!loading && data && (
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-muted-ink" data-testid="ideal-legend">
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "rgba(31,157,85,0.7)" }} /> Excellent (≥ {data.min_avg} BS/j)</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "rgba(238,114,46,0.7)" }} /> Bon potentiel</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: "rgba(100,116,139,0.5)" }} /> Déjà planifié</span>
            <span className="inline-flex items-center gap-1"><span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold" style={{ background: "rgba(31,157,85,0.14)", color: "#166534" }}><Check className="h-2.5 w-2.5" />Perf. asso</span> = historique de l'association de la ligne</span>
            <span className="inline-flex items-center gap-1"><span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-bold" style={{ background: "rgba(148,110,50,0.14)", color: "#8A5A16" }}><Info className="h-2.5 w-2.5" />Réf. XXX</span> = perf. d'une AUTRE association</span>
          </div>
        )}

        {/* Bandeau filtre nouvelles implantations */}
        {!loading && data && onlyNew && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-[rgba(238,114,46,0.28)] bg-[rgba(238,114,46,0.06)] px-3 py-2 text-[12px] leading-snug text-[#8A3A10]" data-testid="ideal-new-banner">
            <Compass className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <b>Nouvelles implantations à conquérir</b> — seuls les magasins qui performent pour une
              <b> autre association</b> (donc sans historique pour l'association de la ligne) sont affichés.
              Ce sont vos meilleures cibles de démarchage. {newCount} créneau{newCount > 1 ? "x" : ""} identifié{newCount > 1 ? "s" : ""}.
            </span>
          </div>
        )}

        {/* Grille lignes × semaines */}
        {loading ? (
          <Skeleton className="h-[360px] rounded-2xl" />
        ) : !data || (data.rows || []).length === 0 ? (
          <div className={`${card} flex flex-col items-center justify-center px-6 py-16 text-center`} data-testid="ideal-empty">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[var(--surface-2)]"><CalendarRange className="h-6 w-6 text-[var(--shariti-orange)]" /></div>
            {assoc !== "all" && (data?.top_spots || []).length > 0 ? (
              <>
                <div className="mt-3 text-sm font-bold">Campagne à venir — pas encore de ligne active</div>
                <p className="mt-1 max-w-md text-xs text-muted-ink">
                  {data?.top_spots_assoc_acronym ? <b>{data.top_spots_assoc_acronym}</b> : "Cette association"} n'a pas encore de ligne active (ex. campagne qui démarre plus tard).
                  Retrouvez juste en dessous ses <b>meilleurs emplacements historiques</b> à rebooker en priorité.
                  Pour planifier semaine par semaine, créez d'abord une ligne dans <b>Administration → Lignes</b>.
                </p>
              </>
            ) : (
              <>
                <div className="mt-3 text-sm font-bold">Aucune proposition à afficher</div>
                <p className="mt-1 max-w-md text-xs text-muted-ink">Aucune ligne opérationnelle ou pas assez d'historique de bulletins sur les emplacements. Alimentez la banque de spots et importez les bulletins pour activer les recommandations.</p>
              </>
            )}
          </div>
        ) : (
          <>
          {/* MOBILE — cartes empilées par ligne (aucun scroll horizontal → aucun chevauchement) */}
          <div className="space-y-3 lg:hidden" data-testid="ideal-mobile">
            {(() => {
              const rowHasEntries = (row) => row.cells.some((c) => {
                if (c.state === "none") return false;
                if (onlyNew) return c.state === "proposed" && c.perf_scope === "global";
                return true;
              });
              const mrows = displayedRows.filter(rowHasEntries);
              if (mrows.length === 0) {
                return (
                  <div className={`${card} flex flex-col items-center justify-center px-6 py-12 text-center`} data-testid="ideal-mobile-empty">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--surface-2)]"><Compass className="h-5 w-5 text-[var(--shariti-orange)]" /></div>
                    <div className="mt-3 text-sm font-bold">Aucune proposition à afficher</div>
                    <p className="mt-1 text-xs text-muted-ink">{onlyNew ? "Aucune nouvelle implantation à conquérir sur cet horizon. Désactivez le filtre pour tout voir." : "Aucune proposition disponible sur cet horizon."}</p>
                  </div>
                );
              }
              return mrows.map((row, ri) => (
                <MobileLigneCard key={row.ligne_id} row={row} weeksMeta={weeksMeta} onlyNew={onlyNew}
                  minAvg={data.min_avg} onOpenSpot={openSpot} onBook={bookWeek} onLock={lockSlot}
                  defaultOpen={ri === 0} />
              ));
            })()}
          </div>

          {/* DESKTOP — grille lignes × semaines */}
          <div className={`${card} hidden overflow-hidden lg:block`}>
            <div className="overflow-auto max-h-[calc(100vh-240px)]">
              <div className="min-w-max">
                <div className="sticky top-0 z-30 shadow-[0_2px_6px_-2px_rgba(0,0,0,0.12)]">
                {isMonthly ? (
                  /* En-tête MOIS (vue compacte) */
                  <div className="flex border-b border-[var(--border-soft)] bg-[var(--surface-2)]">
                    <div className="sticky left-0 z-30 shrink-0 bg-[var(--surface-2)] px-3 py-2.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]"
                      style={{ width: leftW }}>
                      Ligne / Mois
                    </div>
                    {monthSpans.map((m, i) => (
                      <div key={i} className="shrink-0 border-l border-[var(--border-soft)] px-3 py-2.5 text-center"
                        style={{ width: monthColW }}>
                        <div className="text-[12px] font-extrabold capitalize text-ink">{m.label}</div>
                        <div className="text-[10px] text-muted-ink">{m.count} sem.</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                  {/* En-tête mois */}
                  <div className="flex border-b border-[var(--border-soft)] bg-[var(--surface-2)]">
                    <div className="sticky left-0 z-30 shrink-0 bg-[var(--surface-2)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]"
                      style={{ width: leftW }}>
                      Ligne / Semaine
                    </div>
                    {monthSpans.map((m, i) => (
                      <div key={i} className="shrink-0 border-l border-[var(--border-soft)] px-3 py-1.5 text-[12px] font-extrabold capitalize text-ink"
                        style={{ width: m.count * colW }}>
                        {m.label}
                      </div>
                    ))}
                  </div>
                  {/* En-tête semaines — dates RÉELLES */}
                  <div className="flex border-b border-[var(--border-soft)] bg-white">
                    <div className="sticky left-0 z-30 shrink-0 bg-white px-3 py-2 shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]" style={{ width: leftW }} />
                    {weeksMeta.map((w) => (
                      <div key={w.monday} className="shrink-0 border-l border-[var(--border-soft)] px-2 py-2 text-center"
                        style={{ width: colW }}>
                        <div className="text-[12px] font-extrabold capitalize text-ink">{frShortDate(w.monday)}</div>
                        <div className="text-[10px] text-muted-ink">Semaine {w.week_number}</div>
                      </div>
                    ))}
                  </div>
                  </>
                )}
                </div>{/* fin en-tête figé */}
                {/* Lignes */}
                {displayedRows.map((row, ri) => {
                  const rowBg = ri % 2 ? "#FAF9F7" : "#FFFFFF";
                  return (
                  <div key={row.ligne_id} className="flex items-stretch" style={{ background: rowBg }} data-testid={`ideal-row-${row.ligne_id}`}>
                    <div className="sticky left-0 z-20 shrink-0 border-r border-[var(--border-soft)] px-3 py-2.5 shadow-[6px_0_10px_-6px_rgba(0,0,0,0.18)]"
                      style={{ width: leftW, background: rowBg }}>
                      <div className="flex items-start gap-1.5">
                        <span className="mt-0.5 inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-extrabold text-white"
                          style={{ background: row.assoc.color }} title={row.assoc.name}>{row.assoc.acronym || "—"}</span>
                        <span className="break-words text-[12px] font-bold leading-tight text-ink">{row.identifiant}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-muted-ink">
                        {row.cdp?.name && (
                          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full border border-black/20" style={{ background: row.cdp.color || "var(--surface-2)" }} />{row.cdp.name}</span>
                        )}
                        <span className="inline-flex items-center gap-1"><Store className="h-2.5 w-2.5" />{row.canal_nom}</span>
                      </div>
                      <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink">
                        <MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" /><span className="break-words">{row.territoire_label}</span>
                      </div>
                    </div>
                    {isMonthly ? (
                      monthSpans.map((m, mi) => (
                        <div key={mi} className="shrink-0 border-l border-[var(--border-soft)] p-1.5" style={{ width: monthColW }}>
                          <MonthCell
                            items={Array.from({ length: m.count }, (_, kk) => ({ cell: row.cells[m.startIdx + kk], weekMeta: weeksMeta[m.startIdx + kk] }))}
                            onlyNew={onlyNew} minAvg={data.min_avg} onOpenSpot={openSpot} />
                        </div>
                      ))
                    ) : (
                      row.cells.map((cell, ci) => (
                        <div key={ci} className="shrink-0 border-l border-[var(--border-soft)] p-1.5" style={{ width: colW }}>
                          <IdealCell cell={cell} weekMeta={weeksMeta[ci]} onOpenSpot={openSpot} onBook={bookWeek}
                            minAvg={data.min_avg} ligneAssoc={row.assoc} onlyNew={onlyNew}
                            onLock={() => lockSlot(row, cell)} />
                        </div>
                      ))
                    )}
                  </div>
                  );
                })}
                {displayedRows.length === 0 && (
                  <div className="flex items-center justify-center px-6 py-12 text-center" data-testid="ideal-cdp-empty">
                    <div>
                      <div className="text-sm font-bold text-ink">Aucune ligne pour ce CDP</div>
                      <p className="mt-1 text-xs text-muted-ink">Ce CDP n'a pas de ligne opérationnelle sur cet horizon. Choisissez « Tous les CDP » pour tout revoir.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
        )}

        {/* Top emplacements du vivier */}
        {!loading && data && (data.top_spots || []).length > 0 && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-[var(--shariti-orange)]" />
              <h3 className="text-sm font-extrabold text-ink">
                {data.top_spots_scope === "association" && data.top_spots_assoc_acronym
                  ? `Meilleurs emplacements — ${data.top_spots_assoc_acronym}`
                  : "Meilleurs emplacements du vivier"}
              </h3>
              <span className="text-[11px] text-muted-ink">
                {data.top_spots_scope === "association"
                  ? "— historique réel de cette association (à rebooker)"
                  : "— triés par rendement réel"}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {(data.top_spots || []).map((s, i) => {
                const green = s.excellent;
                return (
                  <button key={s.spot_id} onClick={() => openSpot(s.spot_id)} data-testid={`ideal-top-${s.spot_id}`}
                    className={`${card} group flex items-start gap-3 p-3 text-left transition-transform hover:-translate-y-0.5`}>
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl text-sm font-extrabold"
                      style={{ background: green ? "rgba(31,157,85,0.12)" : "rgba(238,114,46,0.12)", color: green ? "#1F7A44" : "#EE722E" }}>
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="break-words text-[12px] font-bold leading-tight text-ink" title={s.spot_name}>{s.spot_name}</div>
                      <div className="mt-0.5 flex items-start gap-1 text-[10px] text-muted-ink"><MapPin className="mt-0.5 h-2.5 w-2.5 shrink-0" /><span className="break-words">{[s.ville, s.dept ? `(${s.dept})` : null].filter(Boolean).join(" ")}</span></div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-extrabold" style={{ color: green ? "#1F7A44" : "#EE722E" }}>{s.avg_per_day}</div>
                      <div className="text-[9px] font-semibold uppercase text-muted-ink">BS/j · {s.days}j</div>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-ink transition-transform group-hover:translate-x-0.5" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
